import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Check, Heart, Loader2, Share2, Copy, MessageSquare, Mail } from "lucide-react";
import AddressCapture, { composeAddress } from "../components/AddressCapture";
import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ReferralLanding() {
  const { code: token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlMode = searchParams.get("mode");
  const refParam = searchParams.get("ref"); // Link tracking - referrer ID
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [errorTitle, setErrorTitle] = useState("Link Not Found");
  const [referralData, setReferralData] = useState(null);
  const [lockedMode, setLockedMode] = useState(null);
  
  const signatureRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    address: "",
    postcode: "",
    addressLine1: "",
    addressLine2: "",
    townCity: "",
    county: "",
    phone: "",
    email: "",
    dobDay: "",
    dobMonth: "",
    dobYear: "",
    amount: "",
    lotteryEntries: "1",
    consentTerms: false,
    consentMarketing: false,
    consentSms: false,
    consentEmail: false,
    consentCall: false,
    consentPost: false,
    signature: "",
    // Package 1A — parity with authenticated flow
    accountHolderName: "",
    sortCode: "",
    accountNumber: "",
    paymentFrequency: "monthly",
    paymentDatePreference: "",
    ddAccepted: false,
    giftAid: false,
  });

  // Package 1A — final-submission idempotency key
  const [idempotencyKey] = useState(() => {
    const rnd = () => (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID().replace(/-/g, "")
      : Math.random().toString(36).slice(2).padEnd(32, "0");
    return (rnd() + rnd()).slice(0, 64);
  });
  
  const [errors, setErrors] = useState({});

  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  useEffect(() => {
    resolveToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, urlMode]);

  const resolveToken = async () => {
    try {
      const response = await axios.get(`${API}/referrals/resolve/${token}`, {
        params: { mode: urlMode }
      });
      if (response.data.isArchived) {
        // OCI V2.38 — honest donor journey: closed campaign, never show a live form
        setErrorTitle("Campaign Closed");
        setError("This campaign is no longer accepting donations. Thank you for your interest.");
        return;
      }
      setReferralData(response.data);
      setLockedMode(response.data.mode); // Mode is locked from URL
    } catch (error) {
      setError("Invalid or expired referral link");
    } finally {
      setLoading(false);
    }
  };

  // Signature canvas
  useEffect(() => {
    if (!loading && !error) {
      initCanvas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error]);

  const initCanvas = () => {
    const canvas = signatureRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#0F172A";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  const getPos = (e) => {
    const canvas = signatureRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const ctx = signatureRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = signatureRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endDraw = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = signatureRef.current;
    setFormData({ ...formData, signature: canvas.toDataURL() });
  };

  const clearSignature = () => {
    const canvas = signatureRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setFormData({ ...formData, signature: "" });
  };

  const calculateAge = () => {
    if (!formData.dobDay || !formData.dobMonth || !formData.dobYear) return 0;
    const today = new Date();
    const birthDate = new Date(formData.dobYear, formData.dobMonth - 1, formData.dobDay);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = "Required";
    if (!formData.lastName.trim()) newErrors.lastName = "Required";
    if (!(formData.addressLine1 || formData.address).trim()) newErrors.address = "Required";
    if (!(formData.townCity || "").trim()) newErrors.townCity = "Required";
    if (!formData.postcode.trim()) newErrors.postcode = "Required";
    if (!formData.dobDay) newErrors.dobDay = "Required";
    if (!formData.dobMonth) newErrors.dobMonth = "Required";
    if (!formData.dobYear) newErrors.dobYear = "Required";
    
    if (formData.dobDay && formData.dobMonth && formData.dobYear) {
      const age = calculateAge();
      if (age < 18) newErrors.dob = "Must be 18 or older";
    }
    
    if (lockedMode !== "lottery" && !formData.amount) {
      newErrors.amount = "Required";
    }
    if (!formData.consentTerms) newErrors.consentTerms = "Required";
    if (!formData.signature) newErrors.signature = "Signature required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error("Please complete all required fields");
      return;
    }

    setSubmitting(true);
    try {
      const _needsBank = (lockedMode === "regular" || lockedMode === "regular_giving" || lockedMode === "lottery");
      await axios.post(`${API}/conversions/referral`, {
        token: token,
        mode: lockedMode,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email || null,
        phone: formData.phone || null,
        address: formData.address || composeAddress(formData),
        postcode: formData.postcode,
        addressLine1: formData.addressLine1 || null,
        addressLine2: formData.addressLine2 || null,
        townCity: formData.townCity || null,
        county: formData.county || null,
        dobDay: parseInt(formData.dobDay),
        dobMonth: parseInt(formData.dobMonth),
        dobYear: parseInt(formData.dobYear),
        amount: formData.amount ? parseFloat(formData.amount) : null,
        lotteryEntries: lockedMode === "lottery" ? parseInt(formData.lotteryEntries) : null,
        consentMarketing: formData.consentMarketing,
        consentTerms: formData.consentTerms,
        consentTermsVersion: formData.consentTerms ? "v1" : null,
        consentTermsAcceptedAt: formData.consentTerms ? new Date().toISOString() : null,
        consentWordingVersion: "v1",
        consentSms: formData.consentSms,
        consentEmail: formData.consentEmail,
        consentCall: formData.consentCall,
        consentPost: formData.consentPost,
        signature: formData.signature,
        // Link tracking
        referrerId: refParam,
        // Package 1A parity fields
        sortCode: _needsBank && formData.sortCode ? formData.sortCode.replace(/[-\s]/g, "") : null,
        accountNumber: _needsBank && formData.accountNumber ? formData.accountNumber.replace(/\s/g, "") : null,
        accountHolderName: _needsBank ? (formData.accountHolderName || null) : null,
        paymentFrequency: formData.paymentFrequency || "monthly",
        paymentDatePreference: _needsBank ? (formData.paymentDatePreference || null) : null,
        ddAccepted: !!(_needsBank && formData.ddAccepted),
        ddAcceptedAt: _needsBank && formData.ddAccepted ? new Date().toISOString() : null,
        ddWordingVersion: _needsBank && formData.ddAccepted ? "v1" : null,
        giftAid: !!formData.giftAid,
        giftAidAcceptedAt: formData.giftAid ? new Date().toISOString() : null,
        idempotencyKey,
      });
      setSubmitted(true);
      toast.success("Thank you for your support!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const modeLabels = {
    lottery: "Lottery Entry",
    regular: "Regular Giving",
    oneoff: "One-Off Donation"
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="referral-loading">
        <Loader2 className="h-8 w-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6" data-testid="referral-error">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <Heart className="h-8 w-8 text-red-500" />
        </div>
        <h1 className="font-heading font-black text-xl text-slate-900 mb-2">{errorTitle}</h1>
        <p className="text-slate-500 text-center">{error}</p>
      </div>
    );
  }

  // Extract branding from referral data (before conditional returns)
  const logoUrl = referralData?.logoUrl;
  const primaryColor = referralData?.primaryColor || "#10b981";
  const brandName = referralData?.brandName || referralData?.campaignName;
  const headerImageUrl = referralData?.headerImageUrl;

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex flex-col" data-testid="referral-success">
        <header className="p-4 border-b border-slate-200">
          {logoUrl ? (
            <img src={logoUrl} alt={brandName} className="h-8 object-contain" />
          ) : (
            <div className="font-heading font-black text-xl text-slate-900">{brandName}</div>
          )}
        </header>
        <main className="flex-1 p-6 max-w-md mx-auto w-full flex flex-col items-center justify-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: `${primaryColor}20` }}>
            <Check className="h-8 w-8" style={{ color: primaryColor }} />
          </div>
          <h1 className="font-heading font-black text-2xl text-slate-900 mb-2 text-center">
            Thank You, {formData.firstName}!
          </h1>
          <p className="text-slate-500 text-center mb-6">Your support means the world to us.</p>
          <div className="rounded-xl p-4 text-center" style={{ backgroundColor: `${primaryColor}10` }}>
            <p className="text-sm" style={{ color: primaryColor }}>
              We've received your {modeLabels[lockedMode]?.toLowerCase()} and will be in touch soon.
            </p>
          </div>
        </main>
      </div>
    );
  }

  // Derive gradient colors from primary
  const gradientStart = primaryColor;
  const gradientEnd = `${primaryColor}dd`;

  // Header style with optional background image
  const headerStyle = headerImageUrl 
    ? { 
        backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(${headerImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }
    : { 
        background: `linear-gradient(135deg, ${gradientStart} 0%, ${gradientEnd} 100%)` 
      };

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}08 50%, #f8fafc 100%)` }} data-testid="referral-form">
      {/* Header with gradient or image */}
      <header className="py-8 px-4" style={headerStyle}>
        <div className="max-w-lg mx-auto text-center">
          {/* Only show logo if no header image - header image already contains branding */}
          {!headerImageUrl && logoUrl ? (
            <img src={logoUrl} alt={brandName} className="h-16 md:h-20 object-contain mx-auto mb-3" style={{ filter: 'brightness(0) invert(1)' }} onError={(e) => { e.target.style.filter = 'none'; }} />
          ) : !headerImageUrl ? (
            <div className="font-heading font-black text-2xl md:text-3xl text-white mb-2">{brandName}</div>
          ) : null}
          <div className="inline-block px-4 py-1.5 rounded-full text-sm font-medium" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}>
            {modeLabels[lockedMode]}
          </div>
        </div>
      </header>

      {/* Form Container */}
      <main className="max-w-lg mx-auto px-4 -mt-4 pb-32">
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-8">
        {/* Personal Details */}
        <section data-testid="section-personal">
          <h2 className="font-heading font-bold text-xl text-slate-900 mb-6">
            Personal Details
          </h2>
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700 font-medium text-sm">First Name *</Label>
                <Input value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} className={`h-12 mt-2 rounded-xl border-slate-200 focus:ring-2 focus:ring-offset-0 ${errors.firstName ? 'border-red-500 focus:ring-red-200' : 'focus:ring-slate-200'}`} data-testid="first-name-input" />
                {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
              </div>
              <div>
                <Label className="text-slate-700 font-medium text-sm">Last Name *</Label>
                <Input value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} className={`h-12 mt-2 rounded-xl border-slate-200 focus:ring-2 focus:ring-offset-0 ${errors.lastName ? 'border-red-500 focus:ring-red-200' : 'focus:ring-slate-200'}`} data-testid="last-name-input" />
                {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
              </div>
            </div>
            <AddressCapture
              value={formData}
              onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
              errors={errors}
              required={true}
              idPrefix="ref-addr"
            />
            <div>
              <Label className="text-slate-700 font-medium text-sm">Phone <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="h-12 mt-2 rounded-xl border-slate-200 focus:ring-2 focus:ring-offset-0 focus:ring-slate-200" data-testid="phone-input" />
            </div>
            <div>
              <Label className="text-slate-700 font-medium text-sm">Email <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="h-12 mt-2 rounded-xl border-slate-200 focus:ring-2 focus:ring-offset-0 focus:ring-slate-200" data-testid="email-input" />
            </div>
          </div>
        </section>

        {/* DOB */}
        <section data-testid="section-dob">
          <h2 className="font-heading font-bold text-xl text-slate-900 mb-4">
            Date of Birth
          </h2>
          <p className="text-slate-500 text-sm mb-5">Required to verify you are at least 18 years old.</p>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-slate-700 font-medium text-sm">Day *</Label>
              <Select value={formData.dobDay} onValueChange={(v) => setFormData({ ...formData, dobDay: v })}>
                <SelectTrigger className={`h-12 mt-2 rounded-xl ${errors.dobDay ? 'border-red-500' : 'border-slate-200'}`} data-testid="dob-day-select">
                  <SelectValue placeholder="Day" />
                </SelectTrigger>
                <SelectContent>{days.map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700 font-medium text-sm">Month *</Label>
              <Select value={formData.dobMonth} onValueChange={(v) => setFormData({ ...formData, dobMonth: v })}>
                <SelectTrigger className={`h-12 mt-2 rounded-xl ${errors.dobMonth ? 'border-red-500' : 'border-slate-200'}`} data-testid="dob-month-select">
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>{months.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700 font-medium text-sm">Year *</Label>
              <Select value={formData.dobYear} onValueChange={(v) => setFormData({ ...formData, dobYear: v })}>
                <SelectTrigger className={`h-12 mt-2 rounded-xl ${errors.dobYear ? 'border-red-500' : 'border-slate-200'}`} data-testid="dob-year-select">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {errors.dob && <p className="text-red-500 text-sm mt-2">{errors.dob}</p>}
        </section>

        {/* Donation - Mode Locked */}
        <section data-testid="section-donation">
          <h2 className="font-heading font-bold text-xl text-slate-900 mb-4">
            Donation
          </h2>
          <div className="rounded-xl p-4 mb-5" style={{ backgroundColor: `${primaryColor}15` }}>
            <p className="text-sm font-semibold" style={{ color: primaryColor }}>
              {modeLabels[lockedMode]}
            </p>
          </div>
          
          {lockedMode === "lottery" ? (
            <div>
              <Label className="text-slate-700 font-medium text-sm">Number of Entries</Label>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <button type="button" onClick={() => setFormData({ ...formData, lotteryEntries: "1" })} className={`h-24 rounded-2xl border-2 font-semibold transition-all ${formData.lotteryEntries === "1" ? 'border-2 shadow-md' : 'border-slate-200 hover:border-slate-300'}`} style={formData.lotteryEntries === "1" ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10`, color: primaryColor } : {}} data-testid="lottery-single-btn">
                  <div className="text-lg">Single</div>
                  <div className="text-sm font-normal text-slate-500 mt-1">1 entry</div>
                </button>
                <button type="button" onClick={() => setFormData({ ...formData, lotteryEntries: "2" })} className={`h-24 rounded-2xl border-2 font-semibold transition-all ${formData.lotteryEntries === "2" ? 'border-2 shadow-md' : 'border-slate-200 hover:border-slate-300'}`} style={formData.lotteryEntries === "2" ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10`, color: primaryColor } : {}} data-testid="lottery-double-btn">
                  <div className="text-lg">Double</div>
                  <div className="text-sm font-normal text-slate-500 mt-1">2 entries</div>
                </button>
              </div>
            </div>
          ) : (
            <div>
              <Label className="text-slate-700 font-medium text-sm">{lockedMode === "regular" ? "Monthly Amount (£) *" : "Donation Amount (£) *"}</Label>
              <Input type="number" min="1" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} className={`h-12 mt-2 rounded-xl border-slate-200 focus:ring-2 focus:ring-offset-0 ${errors.amount ? 'border-red-500 focus:ring-red-200' : 'focus:ring-slate-200'}`} placeholder="0.00" data-testid="amount-input" />
              {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount}</p>}
            </div>
          )}
        </section>

        {/* Consent */}
        <section data-testid="section-consent">
          <h2 className="font-heading font-bold text-xl text-slate-900 mb-4">
            Consent
          </h2>
          <div className="p-4 rounded-xl bg-slate-50 text-sm text-slate-600 mb-5">
            <p>By proceeding, you agree to our Terms of Service and Privacy Policy.</p>
          </div>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Checkbox id="consentTerms" checked={formData.consentTerms} onCheckedChange={(v) => setFormData({ ...formData, consentTerms: v })} className={errors.consentTerms ? 'border-red-500' : ''} data-testid="consent-terms-checkbox" />
              <Label htmlFor="consentTerms" className="text-slate-700 text-sm cursor-pointer leading-relaxed">I agree to the Terms of Service and Privacy Policy *</Label>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox id="consentMarketing" checked={formData.consentMarketing} onCheckedChange={(v) => setFormData({ ...formData, consentMarketing: v })} data-testid="consent-marketing-checkbox" />
              <Label htmlFor="consentMarketing" className="text-slate-700 text-sm cursor-pointer leading-relaxed">I would like to receive marketing communications</Label>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox id="consentSms" checked={formData.consentSms} onCheckedChange={(v) => setFormData({ ...formData, consentSms: v })} data-testid="consent-sms-checkbox" />
              <Label htmlFor="consentSms" className="text-slate-700 text-sm cursor-pointer leading-relaxed">I consent to receiving SMS messages</Label>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox id="consentEmail" checked={formData.consentEmail} onCheckedChange={(v) => setFormData({ ...formData, consentEmail: v })} data-testid="consent-email-checkbox" />
              <Label htmlFor="consentEmail" className="text-slate-700 text-sm cursor-pointer leading-relaxed">I consent to receiving email communications</Label>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox id="consentCall" checked={formData.consentCall} onCheckedChange={(v) => setFormData({ ...formData, consentCall: v })} data-testid="consent-call-checkbox" />
              <Label htmlFor="consentCall" className="text-slate-700 text-sm cursor-pointer leading-relaxed">I consent to receiving phone calls</Label>
            </div>
          </div>
          {errors.consentTerms && <p className="text-red-500 text-sm mt-2">{errors.consentTerms}</p>}
        </section>

        {/* Signature */}
        <section data-testid="section-signature">
          <h2 className="font-heading font-bold text-xl text-slate-900 mb-4">
            Signature
          </h2>
          <p className="text-slate-500 text-sm mb-5">Please sign below to confirm your donation.</p>
          <div className={`border-2 rounded-2xl overflow-hidden ${errors.signature ? 'border-red-500' : 'border-slate-200'}`}>
            <canvas ref={signatureRef} width={350} height={150} className="w-full bg-slate-50 touch-none" onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw} onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw} data-testid="signature-canvas" />
          </div>
          <Button type="button" variant="outline" onClick={clearSignature} className="w-full h-11 mt-3 rounded-xl" data-testid="clear-signature-btn">Clear Signature</Button>
          {errors.signature && <p className="text-red-500 text-sm mt-2">{errors.signature}</p>}
        </section>

        {/* Summary */}
        <section data-testid="section-summary">
          <h2 className="font-heading font-bold text-xl text-slate-900 mb-5">Summary</h2>
          <div className="bg-slate-50 rounded-2xl p-5 space-y-3 mb-4">
            <h3 className="font-semibold text-slate-900">Personal Details</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="text-slate-500">Name</div>
              <div className="text-slate-900 font-medium">{formData.firstName} {formData.lastName}</div>
              <div className="text-slate-500">Address</div>
              <div className="text-slate-900 font-medium">{formData.address}, {formData.postcode}</div>
            </div>
          </div>
          <div className="rounded-2xl p-5 space-y-3" style={{ backgroundColor: `${primaryColor}10` }}>
            <h3 className="font-semibold" style={{ color: primaryColor }}>Donation Details</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div style={{ color: `${primaryColor}aa` }}>Type</div>
              <div className="font-medium" style={{ color: primaryColor }}>{modeLabels[lockedMode]}</div>
              {lockedMode === "lottery" ? (
                <><div style={{ color: `${primaryColor}aa` }}>Entries</div><div className="font-medium" style={{ color: primaryColor }}>{formData.lotteryEntries === "2" ? "Double" : "Single"}</div></>
              ) : (
                <><div style={{ color: `${primaryColor}aa` }}>Amount</div><div className="font-medium" style={{ color: primaryColor }}>{formData.amount ? `£${formData.amount}` : '-'}</div></>
              )}
            </div>
          </div>
        </section>
        </div>
      </main>

      {/* Submit - Fixed CTA */}
      <footer className="fixed bottom-0 left-0 right-0 p-4 bg-white/95 backdrop-blur-sm border-t border-slate-100">
        <div className="max-w-lg mx-auto">
          <Button onClick={handleSubmit} disabled={submitting} className="w-full h-14 text-white font-bold text-lg rounded-2xl shadow-lg transition-all hover:shadow-xl hover:scale-[1.01] active:scale-[0.99]" style={{ backgroundColor: primaryColor }} data-testid="submit-btn">
            {submitting ? <span className="flex items-center gap-2"><span className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Submitting...</span> : <span className="flex items-center gap-2"><Check className="h-5 w-5" />Submit Donation</span>}
          </Button>
        </div>
      </footer>
    </div>
  );
}
